/**
 * SurveyResultsPage — index of completed surveys (VER-109, route: /survey/results).
 *
 * Lists the finished surveys (mock data — see surveyResults.mock.ts) and links each to
 * its per-question results detail. FRONTEND-ONLY: there is no API; this reads the mock
 * COMPLETED_SURVEYS directly.
 *
 * TODO [RBAC]: guarded with permission="DashboardView" in Routes.tsx — swap to the
 * survey-results permit once defined. Run verbal-rbac-review before merge.
 */
import React from "react";
import { Box, Button, Chip, Divider, List, ListItemButton, ListItemText, Stack, Typography } from "@mui/material";
import { AssessmentOutlined, ChevronRightOutlined, GroupOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { COMPLETED_SURVEYS } from "./surveyResults.mock";
import { exportAllXlsx } from "./exportSurvey";
import { ExportMenu } from "./ExportMenu";

const formatDate = (iso: string): string => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? iso
        : date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

export const SurveyResultsPage = (): JSX.Element => {
    const navigate = useNavigate();

    return (
        <Box p={4} width={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                    <AssessmentOutlined color="primary" sx={{ fontSize: "2rem" }} />
                    <Typography variant="h2" color="textPrimary">
                        Survey results
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FileDownloadOutlined />}
                    onClick={() => exportAllXlsx(COMPLETED_SURVEYS)}
                >
                    Export all (Excel)
                </Button>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Completed surveys and their responses. Choose a survey to see per-question results — response
                distributions, averages, and written feedback — or export any survey to CSV, Excel or JSON.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <List disablePadding>
                {COMPLETED_SURVEYS.map((completed) => (
                    <ListItemButton
                        key={completed.id}
                        onClick={() => navigate(`/survey/results/${completed.id}`)}
                        sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}
                    >
                        <ListItemText
                            primary={
                                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {completed.survey.title}
                                    </Typography>
                                    {completed.survey.code ? (
                                        <Chip size="small" variant="outlined" label={completed.survey.code} />
                                    ) : null}
                                </Stack>
                            }
                            secondary={`${completed.programmeName} · completed ${formatDate(completed.completedAt)}`}
                        />
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                                size="small"
                                icon={<GroupOutlined />}
                                label={`${completed.responses.length} responses`}
                            />
                            <ExportMenu completed={completed} variant="icon" />
                            <ChevronRightOutlined color="action" />
                        </Stack>
                    </ListItemButton>
                ))}
            </List>
        </Box>
    );
};
